import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { Select } from 'primeng/select';
import { SharedModule } from 'primeng/api';
import { DevToolJobInfo } from '../../interfaces/dev-tool-job-info';
import { DevToolJobsService } from '../../services/dev-tool-jobs.service';

@Component({
    selector: 'mmoga-dev-tool-job',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        Select,
        SharedModule
    ],
    templateUrl: './dev-tool-job.component.html',
    styleUrl: './dev-tool-job.component.scss'
})
export class DevToolJobComponent implements OnInit {
    private readonly devToolJobsService = inject(DevToolJobsService);
    private readonly toastrService = inject(ToastrService);


    jobs: DevToolJobInfo[] = [];
    selectedJob: DevToolJobInfo | undefined;
    payload = '';
    isTriggering = false;
    isLoadingJobs = false;

    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.loadJobs();
    }

    loadJobs(): void {
        this.isLoadingJobs = true;
        this.devToolJobsService.getAvailableJobs()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (jobs) => {
                    this.jobs = jobs;
                    this.isLoadingJobs = false;
                },
                error: () => {
                    this.toastrService.error('Failed to load available jobs.');
                    this.isLoadingJobs = false;
                }
            });
    }

    triggerJob(): void {
        if (!this.selectedJob || this.isTriggering || this.selectedJob.disabled) {
            return;
        }

        this.isTriggering = true;
        const job = this.selectedJob;
        const trimmedPayload = job.requiresPayload ? this.payload.trim() : '';
        const request$ = trimmedPayload
            ? this.devToolJobsService.triggerJob(job.key, trimmedPayload)
            : this.devToolJobsService.triggerJob(job.key);

        request$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.toastrService.success(`Job '${job.displayName}' triggered.`);
                    if (job.requiresPayload) {
                        this.payload = '';
                    }
                    this.isTriggering = false;
                },
                error: (err) => {
                    const serverMessage = err?.error?.message;
                    const detail = serverMessage ? `: ${serverMessage}` : '';
                    this.toastrService.error(`Failed to trigger '${job.displayName}'${detail}`);
                    this.isTriggering = false;
                }
            });
    }
}
